import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from "react";
import { createPortal } from "react-dom";
import { readKbPref, writeKbPref, KB_PREF_EVENT } from "../Util/kbPrefStorage.js";
import { focusNextField } from "../Util/handleEnterKey.js";
import Combobox from "./Combobox.jsx";
import { buildRankIndex, rankIndex } from "../Util/rankOptions.js";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { markFormDirty } from "../Util/formDirty.js";

/**
 * DatalistSelect
 *
 * Hybrid component:
 *   - Desktop (>768px) → ranked keyboard combobox (Combobox.jsx: prefix-first
 *                         ranking, ↑/↓/Enter/Esc, hint + badge per row, portal list)
 *   - Mobile  (≤768px) → read-only trigger + custom bottom sheet with search + fixed-height scroll
 *                         + fallback virtual keyboard if native keyboard doesn't appear
 *
 * Validation strategy (both variants):
 *   - While typing  → no error
 *   - On blur / dismiss without valid selection → inline red error
 *   - On match      → clear error immediately
 *
 * Props:
 *   options           {object[]}  – Array of data objects
 *   labelKey          {string}    – Key used as displayed text (simple case)
 *   getLabel          {function}  – (option) => string  (overrides labelKey)
 *   getOptionBadge    {function}  – (option) => string | null  Optional purely-visual tag
 *                                   shown beside the option (e.g. "matching"). Never affects
 *                                   the committed value — resolve(option) stays the raw label.
 *   getOptionSub      {function}  – (option) => string | null  Grey hint beside the label
 *                                   (desktop). Party options (label = partyname) default to
 *                                   "city · GSTIN". Also searchable.
 *   getOptionWeight   {function}  – (option) => number  Tie-break when ranking typed
 *                                   matches: higher first (e.g. usage count).
 *   idKey             {string}    – Key for the ID passed back via onChange
 *   inputValue        {string}    – Current display text (controlled)
 *   onChange          {function}  – (displayText: string, matchedId: any | null) => void
 *   listId            {string}    – id for the desktop listbox (auto-generated if omitted)
 *   placeholder       {string}
 *   required          {boolean}
 *   disabled          {boolean}
 *   autoFocus         {boolean}
 *   style             {object}    – Inline styles forwarded to the input
 *   events            {object}    – Extra handlers: { onFocus, onBlur, onWheel, … }
 *   onBlur            {function}  – (displayText, matchedId) => void
 *   transform         {function}  – Optional value transform (desktop only), e.g. v => v.toUpperCase()
 *   invalidMsg        {string}    – Error shown when value isn't in the list
 *   searchPlaceholder {string}    – Placeholder for the search field inside the mobile sheet
 *   sheetTitle        {string}    – Title shown at top of mobile bottom sheet
 *   ariaLabel         {string}    – Accessible name for the input / phone trigger (no visible <label>)
 */

// ── Desktop variant ──────────────────────────────────────────────────────────
// Party-shaped options (label is the partyname) get "city · GSTIN" as a hint.
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const partySub = (o, label) => {
    if (!o || o.partyname == null || label !== String(o.partyname)) return null;
    const city = o.bacity || o.dacity || o.city;
    // Skip placeholder GSTINs (vendors often carry "NA"/"URP"/zeros) — same
    // pattern the backend validates with (rj-be partyModel GSTIN_REGEX).
    const raw = String(o.gst || o.gstno || o.gstin || "").trim().toUpperCase();
    const gst = GSTIN_RE.test(raw) ? raw : null;
    return [city, gst].filter(Boolean).join(" · ") || null;
};

function DesktopDatalistSelect({
    id, className, options, resolve, findMatch, isValid, idKey,
    getOptionBadge, getOptionSub, getOptionWeight, inputValue = "", onChange, onBlur, listId,
    placeholder, required, disabled, autoFocus, style, events = {}, transform,
    invalidMsg, freeSolo, ariaLabel,
}) {
    const inputRef = useRef(null);
    // Error shows after a blur on invalid text and clears on the next match.
    // Derived from inputValue (not the blur event's value), so a pick committed
    // just before focus moves never flashes a stale "invalid" error.
    const [touched, setTouched] = useState(false);
    const pickedRef = useRef(null);
    // The last option picked. Two options can share a label (two "Surat"
    // locations); matching by label on a later blur committed the FIRST one's
    // id, so a later blur keeps the picked one while the text still shows it.
    const lastPickRef = useRef(null);
    const beforeRef = useRef(inputValue);

    const text = inputValue ?? "";
    const idOf = (o) => (o && idKey ? o[idKey] : null);
    const selected = findMatch(text);
    const showError = touched && !freeSolo && !isValid(text);

    useEffect(() => {
        inputRef.current?.setCustomValidity(showError ? invalidMsg : "");
    }, [showError, invalidMsg]);

    const getSub = useCallback(
        (o) => (getOptionSub ? getOptionSub(o) : partySub(o, resolve(o))),
        [getOptionSub, resolve]
    );

    const commit = (val, match) => {
        if (match) setTouched(false);
        onChange?.(val, idOf(match));
    };

    const handleText = (raw) => {
        const val = transform ? transform(raw) : raw;
        pickedRef.current = null;
        commit(val, findMatch(val));
    };

    // Combobox advances focus itself after an Enter/click pick.
    const handlePick = (o) => {
        const label = resolve(o);
        // `typed`: the text at pick time — a Tab pick blurs before the parent
        // re-renders, so blur may still see it rather than the label.
        pickedRef.current = { label, id: idOf(o), typed: text };
        lastPickRef.current = { label, option: o };
        beforeRef.current = label;
        commit(label, o);
        markFormDirty(inputRef.current);
    };

    const handleEnter = ({ inGrid, matches, openList }) => {
        // Blank cell in a grid: let the grid decide (Tally-style leave).
        if (inGrid && !text.trim()) return false;
        if (!text.trim() || freeSolo || selected) {
            focusNextField(inputRef.current);
            return true;
        }
        if (matches.length) openList();
        else setTouched(true);
        return true;
    };

    const handleEscape = () => {
        const prev = beforeRef.current ?? "";
        if (prev !== text) commit(prev, findMatch(prev));
    };

    const handleBlur = (e) => {
        events.onBlur?.(e);
        // A pick counts only while the input still shows it (or hasn't
        // re-rendered yet). A chip-adder clears the input on pick, and that
        // label is no longer this field's value.
        const p = pickedRef.current;
        const picked = p && (text === p.label || text === p.typed) ? p : null;
        pickedRef.current = null;
        let val = picked ? picked.label : text;
        const earlier = lastPickRef.current;
        let match = picked ? null : (earlier && earlier.label === val ? earlier.option : findMatch(val));
        // Full name typed in another case ("arvind textiles"): commit the one
        // option it unambiguously names rather than flagging it invalid.
        if (!picked && !match && !freeSolo && val.trim()) {
            const q = val.trim().toLowerCase();
            const hits = options.filter((o) => resolve(o).toLowerCase() === q);
            if (hits.length === 1) {
                match = hits[0];
                val = resolve(match);
                commit(val, match);
            }
        }
        const matchedId = picked ? picked.id : idOf(match);
        if (freeSolo) {
            onBlur?.(val, matchedId);
        } else if (picked || match) {
            onBlur?.(val, matchedId);
        } else if (!isValid(val)) {
            setTouched(true);
        }
    };

    const errorStyle = showError
        ? { borderColor: "#dc3545", boxShadow: "0 0 0 0.2rem rgba(220,53,69,.15)" }
        : {};

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Combobox
                id={id}
                listId={listId}
                inputRef={inputRef}
                className={className}
                text={text}
                onTextChange={handleText}
                selectedKey={selected ? (idKey ? selected[idKey] : resolve(selected)) : null}
                options={options}
                getLabel={resolve}
                getKey={(o) => (idKey ? o[idKey] : resolve(o))}
                getSub={getSub}
                getWeight={getOptionWeight}
                getBadge={getOptionBadge}
                onPick={handlePick}
                onEnter={handleEnter}
                onEscape={handleEscape}
                onBlur={handleBlur}
                onFocus={(e) => {
                    beforeRef.current = text;
                    events.onFocus?.(e);
                }}
                onReactKeyDown={events.onKeyDown}
                onWheel={events.onWheel}
                allowFree={freeSolo}
                error={showError}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                autoFocus={autoFocus}
                style={{ ...style, ...errorStyle }}
                inputProps={ariaLabel ? { "aria-label": ariaLabel } : undefined}
            />
            {showError && <span style={styles.errorMsg}>{invalidMsg}</span>}
        </div>
    );
}

// ── Mobile list sizing ────────────────────────────────────────────────────────
const ITEM_HEIGHT   = 44; // = --menu-row-h on phones (styles/tokens.css)
const VISIBLE_ITEMS = 5.5;

const TAP_SLOP = 10; // px of finger travel tolerated before a touch counts as a scroll

const KB_DETECT_DELAY = 600; // ms to wait before checking if native kb opened
const NATIVE_KB_MIN   = 80;  // px the viewport must shrink by to count as "native kb is up"

// Full (keyboard-less) viewport height, shared by every picker. Measured only
// while nothing editable is focused, so it is never taken with a keyboard up —
// comparing against it (not the height at sheet-open) still detects a native
// keyboard that was already open, or that opened before the effect ran.
const isEditable = (el) =>
    !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
const currentViewportHeight = () =>
    window.visualViewport ? window.visualViewport.height : window.innerHeight;
let fullViewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
if (typeof window !== "undefined") {
    const track = () => {
        if (!isEditable(document.activeElement)) fullViewportHeight = currentViewportHeight();
    };
    (window.visualViewport || window).addEventListener("resize", track);
    window.addEventListener("orientationchange", () => setTimeout(track, 300));
}
const nativeKbUp = () =>
    Math.max(fullViewportHeight, window.innerHeight) - currentViewportHeight() >= NATIVE_KB_MIN;

export default function DatalistSelect({
    id,
    className,
    options           = [],
    labelKey          = "name",
    getLabel,
    getOptionBadge,
    getOptionSub,
    getOptionWeight,
    idKey,
    inputValue        = "",
    onChange,
    listId: listIdProp,
    placeholder       = "",
    required          = false,
    disabled          = false,
    autoFocus         = false,
    style,
    onBlur,
    events            = {},
    transform,
    invalidMsg        = "Please select a valid option from the list",
    searchPlaceholder = "Search...",
    sheetTitle        = "Select an option",
    freeSolo          = false,
    ariaLabel,
}) {
    const triggerRef = useRef(null);
    const validityRef = useRef(null);
    const searchRef  = useRef(null);
    const proxyRef   = useRef(null);
    const listRef    = useRef(null);
    const touchStartRef = useRef(null);

    const [open,         setOpen]         = useState(false);
    const [query,        setQuery]        = useState("");
    const [error,        setError]        = useState("");
    const [kbPref,       setKbPref]       = useState(readKbPref);   // "auto" | "on" | "off"
    const [showVirtualKb, setShowVirtualKb] = useState(false);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth <= 768 : false
    );

    // ── responsive detection ─────────────────────────────────────────────────
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // ── focus transfer + native keyboard detection (mobile only) ─────────────
    useEffect(() => {
        if (!open || !isMobile) return;

        const doFocus = () => {
            if (searchRef.current) {
                searchRef.current.focus({ preventScroll: true });
            }
        };
        doFocus();
        const focusRetry = setTimeout(doFocus, 16);

        // Re-read storage on every open: a long-mounted picker's state may predate
        // a change made on Change Password or in another picker's sheet.
        const pref = readKbPref();

        // Apply explicit user preference immediately — skip detection.
        if (pref === "on") {
            setShowVirtualKb(true);
            return () => clearTimeout(focusRetry);
        }
        if (pref === "off") {
            setShowVirtualKb(false);
            return () => clearTimeout(focusRetry);
        }

        // "auto" — show the fallback only if the native keyboard hasn't appeared
        // after a grace period. Old Vivo Androids notoriously fail to pop it here.
        const detectTimer = setTimeout(() => {
            if (!nativeKbUp()) setShowVirtualKb(true);
        }, KB_DETECT_DELAY);

        // A slow native keyboard that opens after the grace period replaces the
        // fallback instead of stacking on top of it (the "two keyboards" bug).
        const vv = window.visualViewport || window;
        const onResize = () => {
            if (nativeKbUp()) {
                clearTimeout(detectTimer);
                setShowVirtualKb(false);
            }
        };
        vv.addEventListener("resize", onResize);

        return () => {
            clearTimeout(focusRetry);
            clearTimeout(detectTimer);
            vv.removeEventListener("resize", onResize);
        };
    }, [open, isMobile, kbPref]);

    // Keep this instance's pref in sync with changes made elsewhere.
    useEffect(() => {
        const sync = () => setKbPref(readKbPref());
        window.addEventListener(KB_PREF_EVENT, sync);
        window.addEventListener("storage", sync);
        return () => {
            window.removeEventListener(KB_PREF_EVENT, sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    // Hide virtual keyboard when sheet closes
    useEffect(() => {
        if (!open) setShowVirtualKb(false);
    }, [open]);

    // ── shared helpers ───────────────────────────────────────────────────────
    const resolve = useCallback(
        (o) => (getLabel ? getLabel(o) : String(o[labelKey])),
        [getLabel, labelKey]
    );

    const findMatch = useCallback(
        (val) => options.find((o) => resolve(o) === val),
        [options, resolve]
    );

    const isValid = useCallback(
        (val) => !val || !!findMatch(val),
        [findMatch]
    );

    // The validity twin also refuses text that isn't one of the options.
    useEffect(() => {
        validityRef.current?.setCustomValidity(!freeSolo && inputValue && !isValid(inputValue) ? (invalidMsg || "Pick from the list") : "");
    }, [inputValue, freeSolo, isValid, invalidMsg]);

    // ── filtered list (mobile search) ────────────────────────────────────────
    // Same hint + ranking as the desktop combobox (prefix first, hint searchable).
    const getSub = useCallback(
        (o) => (getOptionSub ? getOptionSub(o) : partySub(o, resolve(o))),
        [getOptionSub, resolve]
    );
    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        return rankIndex(buildRankIndex(options, { getLabel: resolve, getSub, getWeight: getOptionWeight }), query, Infinity);
    }, [options, query, resolve, getSub, getOptionWeight]);

    // ────────────────────────────────────────────────────────────────────────
    // MOBILE handlers (custom sheet)
    // ────────────────────────────────────────────────────────────────────────
    const openSheet = useCallback(() => {
        if (disabled) return;
        // Immediately focus hidden proxy so iOS opens virtual keyboard
        // synchronously within the tap gesture.
        if (proxyRef.current) {
            proxyRef.current.focus();
        }
        setQuery("");
        setOpen(true);
    }, [disabled]);

    const closeSheet = () => {
        setOpen(false);
        setQuery("");
    };

    const handleDismiss = () => {
        if (!freeSolo && !isValid(inputValue)) setError(invalidMsg);
        closeSheet();
        triggerRef.current?.focus();
    };

    const handleSelect = (option) => {
        const label = resolve(option);
        const oid   = idKey ? option[idKey] : null;
        setError("");
        onChange?.(label, oid);
        onBlur?.(label, oid);
        markFormDirty(triggerRef.current);
        closeSheet();
        triggerRef.current?.focus();
    };

    const commitFreeSolo = useCallback((raw) => {
        const val = transform ? transform(raw) : raw;
        setError("");
        onChange?.(val, null);
        onBlur?.(val, null);
        markFormDirty(triggerRef.current);
        closeSheet();
        triggerRef.current?.focus();
    }, [transform, onChange, onBlur]);

    const handleListKeyDown = (e) => {
        if (e.key === "Escape") { handleDismiss(); return; }
        if (!["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) return;
        const items  = listRef.current?.querySelectorAll("[data-option]");
        const active = listRef.current?.querySelector("[data-option]:focus");
        if (e.key === "Enter" && !active && freeSolo && query.trim() && !findMatch(query)) {
            e.preventDefault();
            commitFreeSolo(query);
            return;
        }
        if (!items?.length) return;
        e.preventDefault();
        const idx    = Array.from(items).indexOf(active);
        if (e.key === "Enter" && active)    active.click();
        else if (e.key === "ArrowDown")     (items[idx + 1] ?? items[0]).focus();
        else if (e.key === "ArrowUp")       (items[idx - 1] ?? items[items.length - 1]).focus();
    };

    // ── react-simple-keyboard handler (mobile only) ───────────────────────────
    const keyboardRef = useRef(null);
    const handleKeyboardChange = useCallback((input) => {
        setQuery(input);
        searchRef.current?.focus({ preventScroll: true });
    }, []);
    // Keep react-simple-keyboard in sync when the native keyboard types into the search input
    useEffect(() => {
        if (keyboardRef.current) keyboardRef.current.setInput(query);
    }, [query]);

    // ── shared error border style ────────────────────────────────────────────
    const errorStyle = error
        ? { borderColor: "#dc3545", boxShadow: "0 0 0 0.2rem rgba(220,53,69,.15)" }
        : {};

    // ────────────────────────────────────────────────────────────────────────
    // DESKTOP render — ranked keyboard combobox (see Combobox.jsx)
    // ────────────────────────────────────────────────────────────────────────
    if (!isMobile) {
        return (
            <DesktopDatalistSelect
                id={id}
                className={className}
                options={options}
                resolve={resolve}
                findMatch={findMatch}
                isValid={isValid}
                idKey={idKey}
                getOptionBadge={getOptionBadge}
                getOptionSub={getOptionSub}
                getOptionWeight={getOptionWeight}
                inputValue={inputValue}
                onChange={onChange}
                onBlur={onBlur}
                listId={listIdProp}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                autoFocus={autoFocus}
                style={style}
                events={events}
                transform={transform}
                invalidMsg={invalidMsg}
                freeSolo={freeSolo}
                ariaLabel={ariaLabel}
            />
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    // MOBILE render — read-only trigger + bottom sheet portal
    // ────────────────────────────────────────────────────────────────────────
    const SearchBox = (
        <input
            ref={searchRef}
            type="text"
            inputMode="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleListKeyDown}
            placeholder={searchPlaceholder}
            style={styles.search}
            autoComplete="off"
        />
    );

    const showFreeSoloRow =
        freeSolo && query.trim().length > 0 && !findMatch(query);
    const freeSoloLabel = transform ? transform(query) : query;

    const FreeSoloRow = showFreeSoloRow ? (
        <li
            role="option"
            data-option
            tabIndex={0}
            style={{ ...styles.item, ...styles.freeSoloItem }}
            onMouseDown={(e) => { e.preventDefault(); commitFreeSolo(query); }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault(); commitFreeSolo(query);
                }
            }}
        >
            Use &ldquo;<strong>{freeSoloLabel}</strong>&rdquo;
        </li>
    ) : null;

    const OptionList = (
        <ul ref={listRef} role="listbox" style={styles.list} onKeyDown={handleListKeyDown}>
            {FreeSoloRow}
            {filtered.length === 0 ? (
                !showFreeSoloRow && <li style={styles.noResults}>No results found</li>
            ) : (
                filtered.map((o, i) => {
                    const label    = resolve(o);
                    const isActive = label === inputValue;
                    const badge    = getOptionBadge ? getOptionBadge(o) : null;
                    return (
                        <li
                            key={idKey ? o[idKey] : i}
                            role="option"
                            aria-selected={isActive}
                            data-option
                            tabIndex={0}
                            style={{ ...styles.item, ...(isActive ? styles.itemActive : {}) }}
                            onMouseDown={(e) => { e.preventDefault(); handleSelect(o); }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault(); handleSelect(o);
                                }
                            }}
                        >
                            {label}
                            {badge && <span style={styles.optionBadge}>{badge}</span>}
                            {(() => {
                                const sub = getSub(o);
                                return sub ? <div style={styles.optionSub}>{sub}</div> : null;
                            })()}
                        </li>
                    );
                })
            )}
        </ul>
    );

    const VirtualKeyboard = showVirtualKb ? (
        <div style={styles.vkbWrap}>
            <Keyboard
                keyboardRef={(r) => (keyboardRef.current = r)}
                onChange={handleKeyboardChange}
                input={query}
                layout={{
                    default: [
                        "1 2 3 4 5 6 7 8 9 0",
                        "q w e r t y u i o p",
                        "a s d f g h j k l",
                        "{shift} z x c v b n m {bksp}",
                        "{space}",
                    ],
                    shift: [
                        "! @ # $ % ^ & * ( )",
                        "Q W E R T Y U I O P",
                        "A S D F G H J K L",
                        "{shift} Z X C V B N M {bksp}",
                        "{space}",
                    ],
                }}
                display={{
                    "{bksp}": "⌫",
                    "{shift}": "⇧",
                    "{space}": "space",
                }}
                theme="hg-theme-default"
                preventMouseDownDefault={true}
            />
        </div>
    ) : null;

    const MobileSheet = open
        ? createPortal(
            <div
                role="dialog"
                aria-modal="true"
                style={styles.overlay}
                onMouseDown={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
            >
                <div style={styles.sheet}>
                    <div style={styles.handleWrap}><div style={styles.handle} /></div>
                    <div style={styles.sheetHeader}>
                        <span style={styles.sheetTitle}>{sheetTitle}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <button
                                type="button"
                                style={{
                                    ...styles.closeBtn,
                                    ...(showVirtualKb ? { color: "var(--coral)" } : {}),
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    // Flip and persist: user explicitly wants kb on/off
                                    const next = showVirtualKb ? "off" : "on";
                                    setKbPref(next);
                                    writeKbPref(next);
                                    setShowVirtualKb(!showVirtualKb);
                                    // Re-focus the search input so typing keeps working
                                    requestAnimationFrame(() => {
                                        searchRef.current?.focus({ preventScroll: true });
                                    });
                                }}
                                aria-label={showVirtualKb ? "Hide on-screen keyboard" : "Show on-screen keyboard"}
                                title={showVirtualKb ? "Hide on-screen keyboard" : "Show on-screen keyboard"}
                            >
                                ⌨
                            </button>
                            <button style={styles.closeBtn} onMouseDown={handleDismiss} aria-label="Close"><i className="bi bi-x" /></button>
                        </div>
                    </div>
                    <div style={styles.searchWrap}>{SearchBox}</div>
                    <div style={{
                        ...styles.listWrap,
                        // Shrink the list when virtual keyboard is showing
                        ...(showVirtualKb ? { height: `${ITEM_HEIGHT * 3}px` } : {}),
                    }}>{OptionList}</div>
                    {VirtualKeyboard}
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ position: "relative", display: "inline-block", width: style?.width ?? "100%" }}>
                <input
                    ref={triggerRef}
                    id={id}
                    className={className}
                    readOnly
                    value={inputValue}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    aria-label={ariaLabel}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    onClick={openSheet}
                    onTouchStart={(e) => {
                        const t = e.touches[0];
                        touchStartRef.current = { x: t.clientX, y: t.clientY, moved: false };
                    }}
                    onTouchMove={(e) => {
                        if (!touchStartRef.current) return;
                        const t = e.touches[0];
                        const dx = Math.abs(t.clientX - touchStartRef.current.x);
                        const dy = Math.abs(t.clientY - touchStartRef.current.y);
                        if (dx > TAP_SLOP || dy > TAP_SLOP) touchStartRef.current.moved = true;
                    }}
                    onTouchEnd={(e) => {
                        const info = touchStartRef.current;
                        touchStartRef.current = null;
                        if (info && info.moved) return;   // was a scroll — ignore
                        e.preventDefault();                // genuine tap: suppress synthetic click
                        openSheet();
                    }}
                    onKeyDown={(e) => {
                        if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
                            e.preventDefault(); openSheet();
                        }
                        if (open && e.key === "Escape") handleDismiss();
                    }}
                    onFocus={events.onFocus}
                    onBlur={(e) => {
                        if (!open) {
                            if (!freeSolo && !isValid(inputValue)) setError(invalidMsg);
                            events.onBlur?.(e);
                        }
                    }}
                    style={{
                        ...style,
                        cursor: disabled ? "not-allowed" : "pointer",
                        userSelect: "none",
                        paddingRight: 28,
                        ...errorStyle,
                    }}
                    autoComplete="off"
                />
                <span style={styles.chevron} aria-hidden="true">{open ? "▲" : "▼"}</span>
                {/* Browsers skip validation on readOnly fields, so a required
                    phone picker never blocked submit. This invisible twin carries
                    `required` + the "pick from the list" rule for the form. */}
                {required && !disabled && (
                    <input
                        ref={validityRef}
                        aria-hidden="true"
                        tabIndex={-1}
                        required
                        value={inputValue || ""}
                        onChange={() => {}}
                        onInvalid={() => { if (!freeSolo && inputValue && !isValid(inputValue)) setError(invalidMsg); }}
                        onFocus={() => triggerRef.current?.focus()}
                        style={styles.validityTwin}
                    />
                )}
            </div>
            {/* Hidden proxy input — captures iOS keyboard focus synchronously */}
            <input
                ref={proxyRef}
                aria-hidden="true"
                tabIndex={-1}
                style={styles.proxyInput}
                autoComplete="off"
            />
            {error && <span style={styles.errorMsg}>{error}</span>}
            {MobileSheet}
        </div>
    );
}

// ── styles ─────────────────────────────────────────────────────────────────────
const styles = {
    validityTwin: {
        position: "absolute", left: 8, bottom: 0, width: 1, height: 1,
        opacity: 0, padding: 0, border: 0, margin: 0, pointerEvents: "none",
    },
    proxyInput: {
        position: "absolute", opacity: 0, height: 0, width: 0,
        padding: 0, border: "none", margin: 0, overflow: "hidden",
        pointerEvents: "none",
    },
    chevron: {
        position: "absolute", right: 10, top: "50%",
        transform: "translateY(-50%)", fontSize: "var(--fs-micro)",
        color: "var(--ink-muted)", pointerEvents: "none",
    },
    errorMsg: {
        fontSize: "var(--fs-hint)", color: "var(--danger)",
        marginTop: 2, lineHeight: 1.3,
    },
    overlay: {
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)", display: "flex",
        flexDirection: "column", justifyContent: "flex-end",
        animation: "dlsFadeIn 0.18s ease",
    },
    sheet: {
        background: "#fff", borderRadius: "16px 16px 0 0",
        display: "flex", flexDirection: "column",
        animation: "dlsSlideUp 0.22s cubic-bezier(0.32,0.72,0,1)",
        maxHeight: "90vh",
    },
    handleWrap: {
        display: "flex", justifyContent: "center",
        padding: "10px 0 4px", flexShrink: 0,
    },
    handle: {
        width: 40, height: 4, borderRadius: 2, background: "var(--border)",
    },
    sheetHeader: {
        display: "flex", alignItems: "center",
        justifyContent: "space-between", padding: "4px 16px 12px",
        flexShrink: 0,
    },
    sheetTitle: {
        fontSize: "var(--fs-section)", fontWeight: 600, color: "var(--ink)",
    },
    closeBtn: {
        background: "none", border: "none", fontSize: "var(--fs-section)",
        color: "var(--ink-muted)", cursor: "pointer",
        padding: "4px 6px", lineHeight: 1,
    },
    searchWrap: {
        padding: "0 12px 8px", flexShrink: 0,
    },
    search: {
        width: "100%", height: "var(--control-h)",
        border: "var(--control-border) solid var(--border)",
        borderRadius: "var(--radius-control)", padding: "0 12px",
        fontSize: "var(--fs-control)", color: "var(--ink-body)",
        outline: "none", background: "var(--paper)", boxSizing: "border-box",
    },
    listWrap: {
        height: `${ITEM_HEIGHT * VISIBLE_ITEMS}px`,
        overflowY: "auto", overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        boxShadow: "inset 0 -8px 8px -8px rgba(0,0,0,0.08)",
    },
    list: { listStyle: "none", margin: 0, padding: 0 },
    item: {
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "var(--menu-row-pad)", fontSize: "var(--fs-body)", color: "var(--ink-body)",
        borderBottom: "1px solid var(--paper-sunk)", cursor: "pointer",
        outline: "none", minHeight: `${ITEM_HEIGHT}px`, boxSizing: "border-box",
    },
    itemActive: {
        background: "var(--coral-tint)", color: "var(--coral-deep)", fontWeight: 600,
    },
    freeSoloItem: {
        background: "#fffbeb", color: "#b45309", /* DESIGN warning tint: "use what I typed" */
    },
    optionSub: {
        fontSize: "var(--fs-hint)", fontWeight: 400, color: "var(--ink-muted)", marginTop: 2,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
    optionBadge: {
        marginLeft: 8, fontSize: "var(--fs-micro)", fontWeight: 600,
        color: "#b45309", background: "#fffbeb",
        border: "1px solid #fde68a", borderRadius: 4,
        padding: "1px 6px", verticalAlign: "middle",
    },
    noResults: {
        padding: "16px", fontSize: "var(--fs-body)", color: "var(--ink-muted)", textAlign: "center",
    },
    vkbWrap: {
        flexShrink: 0,
        borderTop: "1px solid var(--border)",
    },
};

// Sheet keyframes (dlsFadeIn / dlsSlideUp) live in styles/menus.css.